import { Box, Button, Chip, Typography } from "@mui/material";
import { PlayerInfo, TeamId } from "../interface/GameTypes";
import { useEffect } from "react";

type Props = {
  players: PlayerInfo[];
  /** Hide A/B columns for Rummy-style table lobby. */
  seatingMode: "teams" | "table";
  onDismiss: () => void;
  /** Auto-dismiss ms (0 = none). */
  autoDismissMs?: number;
};

export function TeamShuffleFlash({
  players,
  seatingMode,
  onDismiss,
  autoDismissMs = 12000,
}: Props) {
  useEffect(() => {
    if (!autoDismissMs) return undefined;
    const t = window.setTimeout(onDismiss, autoDismissMs);
    return () => window.clearTimeout(t);
  }, [autoDismissMs, onDismiss]);

  const teamA = players.filter((p) => p.teamId === TeamId.A);
  const teamB = players.filter((p) => p.teamId === TeamId.B);
  const tableOrder = seatingMode === "table";

  return (
    <Box
      role="presentation"
      onClick={onDismiss}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 16000,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        pt: { xs: "calc(56px + env(safe-area-inset-top, 0px))", sm: "calc(64px + env(safe-area-inset-top, 0px))" },
        px: 1.5,
        pointerEvents: "auto",
        background: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 45%, transparent 100%)",
        animation: "teamShuffleBackdrop 0.45s ease-out both",
        "@keyframes teamShuffleBackdrop": {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      <Box
        role="dialog"
        aria-labelledby="team-shuffle-title"
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: "100%",
          maxWidth: 520,
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid rgba(255,215,0,0.45)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06) inset",
          background: "linear-gradient(145deg, rgba(26,26,46,0.98) 0%, rgba(15,20,40,0.98) 100%)",
          animation: "teamShuffleCardIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) both",
          "@keyframes teamShuffleCardIn": {
            from: { opacity: 0, transform: "translateY(-16px) scale(0.96)" },
            to: { opacity: 1, transform: "translateY(0) scale(1)" },
          },
        }}
      >
        <Box
          sx={{
            py: 0.75,
            px: 0,
            overflow: "hidden",
            background: "linear-gradient(90deg, #1a237e, #4a148c, #b71c1c, #4a148c, #1a237e)",
            backgroundSize: "200% 100%",
            animation: "teamShuffleMarqueeBg 4s linear infinite",
            "@keyframes teamShuffleMarqueeBg": {
              "0%": { backgroundPosition: "0% 50%" },
              "100%": { backgroundPosition: "200% 50%" },
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              whiteSpace: "nowrap",
              animation: "teamShuffleScroll 14s linear infinite",
              "@keyframes teamShuffleScroll": {
                "0%": { transform: "translateX(0)" },
                "100%": { transform: "translateX(-50%)" },
              },
            }}
          >
            {Array.from({ length: 2 }).map((_, dup) => (
              <Typography
                key={dup}
                id={dup === 0 ? "team-shuffle-title" : undefined}
                component="div"
                sx={{
                  flexShrink: 0,
                  pl: 3,
                  pr: 6,
                  fontWeight: 800,
                  fontSize: { xs: "0.95rem", sm: "1.05rem" },
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "#fff",
                  textShadow: "0 1px 2px rgba(0,0,0,0.6)",
                }}
              >
                New teams · Lineup updated · New teams · Lineup updated ·{" "}
              </Typography>
            ))}
          </Box>
        </Box>

        <Box sx={{ px: 2, py: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: "rgba(255,255,255,0.65)", textAlign: "center", mb: 1.5, fontWeight: 600 }}
          >
            {tableOrder ? "Seat order changed — who sits where" : "Who is on which side this round"}
          </Typography>

          {tableOrder ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, justifyContent: "center" }}>
              {[...players]
                .sort((a, b) => a.seatIndex - b.seatIndex)
                .map((p) => (
                  <Chip
                    key={p.uuid}
                    label={p.name?.trim() || `Seat ${p.seatIndex}`}
                    size="small"
                    sx={{ backgroundColor: "rgba(255,255,255,0.1)", color: "#fff" }}
                  />
                ))}
            </Box>
          ) : (
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
              <Box
                sx={{
                  flex: "1 1 200px",
                  maxWidth: 240,
                  p: 1.25,
                  borderRadius: 2,
                  border: "1px solid rgba(76,175,80,0.45)",
                  backgroundColor: "rgba(76,175,80,0.08)",
                }}
              >
                <Typography variant="caption" sx={{ color: "#81c784", fontWeight: 700, letterSpacing: 1 }}>
                  TEAM A
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.75 }}>
                  {[...teamA]
                    .sort((a, b) => a.seatIndex - b.seatIndex)
                    .map((p) => (
                      <Typography key={p.uuid} variant="body2" sx={{ color: "#fff", fontWeight: 600 }}>
                        {p.name?.trim() || `Seat ${p.seatIndex}`}
                      </Typography>
                    ))}
                </Box>
              </Box>
              <Box
                sx={{
                  flex: "1 1 200px",
                  maxWidth: 240,
                  p: 1.25,
                  borderRadius: 2,
                  border: "1px solid rgba(255,193,7,0.45)",
                  backgroundColor: "rgba(255,193,7,0.08)",
                }}
              >
                <Typography variant="caption" sx={{ color: "#ffd54f", fontWeight: 700, letterSpacing: 1 }}>
                  TEAM B
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.75 }}>
                  {[...teamB]
                    .sort((a, b) => a.seatIndex - b.seatIndex)
                    .map((p) => (
                      <Typography key={p.uuid} variant="body2" sx={{ color: "#fff", fontWeight: 600 }}>
                        {p.name?.trim() || `Seat ${p.seatIndex}`}
                      </Typography>
                    ))}
                </Box>
              </Box>
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button variant="contained" color="primary" size="small" onClick={onDismiss} sx={{ minWidth: 120 }}>
              Got it
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
